import i18n from "i18next";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

// `slate.` is the ESTATE storage namespace, not a brand — coil already writes this same key at
// my.app.co.il (same docit-ui source), so every existing language choice survives the appcoil
// cutover. Do not re-prefix or alias this key for a site profile: it would mass-sign-out /
// reset every my.app.co.il session and risk a pre-paint/key-drift mismatch.
export const LANGUAGE_STORAGE_KEY = "slate.language";

export interface LanguageOption {
  code: string;
  /** Endonym — a language picker that says "Hebrew" in English helps nobody in Israel. */
  label: string;
  dir: "ltr" | "rtl";
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "he", label: "עברית", dir: "rtl" },
];

const RTL_LANGUAGES = new Set(["he", "ar", "fa", "ur"]);

export function directionFor(language: string): "ltr" | "rtl" {
  return RTL_LANGUAGES.has(language.split("-")[0] ?? language) ? "rtl" : "ltr";
}

/** The stored/negotiated language, used before i18next has initialised. */
export function detectLanguage(): string {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      return stored;
    }
  } catch {
    // Private-mode browsers throw on localStorage access; fall through to navigator.
  }
  // Per-host default (Hebrew-face precedent, kms.app.co.il): the same bundle serves
  // desk.docit.pro AND the app.co.il internal host — on an app.co.il host the default is
  // Hebrew. A stored explicit choice above always wins.
  if (typeof window !== "undefined" && window.location.hostname.endsWith(".app.co.il")) {
    return "he";
  }
  const navigatorLanguage = typeof navigator !== "undefined" ? navigator.language : "en";
  const base = navigatorLanguage.split("-")[0] ?? "en";
  return SUPPORTED_LANGUAGES.some((l) => l.code === base) ? base : "en";
}

/**
 * The single side effect of a language change: the document's `lang` and `dir`. Everything
 * else in the shell is written with logical CSS properties, so RTL needs no further work.
 */
export function applyDocumentLanguage(language: string): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = language;
  document.documentElement.dir = directionFor(language);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Persistence is best-effort.
  }
}

/** Catalog pair the CONSUMING app supplies — the package owns mechanics, apps own words. */
export interface I18nResources {
  en: object;
  he: object;
}

export function initI18n(resources: I18nResources, language = detectLanguage()) {
  if (i18n.isInitialized) {
    return i18n;
  }

  i18n
    // en/he ship in the bundle so the first paint is never untranslated; the HTTP backend
    // exists so a new locale dropped into public/locales needs no code change.
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
      lng: language,
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
      defaultNS: "translation",
      ns: ["translation"],
      resources: {
        en: { translation: resources.en },
        he: { translation: resources.he },
      },
      partialBundledLanguages: true,
      backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
      // Natural-language keys ("Toggle sidebar") must not be split on dots or colons.
      keySeparator: false,
      nsSeparator: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      debug: false,
    });

  i18n.on("languageChanged", applyDocumentLanguage);
  applyDocumentLanguage(i18n.language || language);

  return i18n;
}

export { i18n };
